import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { Apple, Globe, Loader2, WalletMinimal } from "lucide-react"

import RuixenPaymentCard from "@/components/ui/modern-payment-form"
import { useOnboardingFlow } from "@/features/onboarding/OnboardingFlowProvider"
import { useSession } from "@/lib/hooks/useSession"

const mockCardNumber = "4242 4242 4242 4242"
const mockExpiry = "12/28"
const mockCvv = "123"

const PaymentPage = () => {
  const { t } = useTranslation(["onboarding", "common"])
  const navigate = useNavigate()
  const flow = useOnboardingFlow()
  const session = useSession()

  const [cardholderName, setCardholderName] = useState<string>(
    flow.tenantName ?? flow.email ?? ""
  )
  const [cardNumber, setCardNumber] = useState<string>(mockCardNumber)
  const [expiry, setExpiry] = useState<string>(mockExpiry)
  const [cvv, setCvv] = useState<string>(mockCvv)
  const [processing, setProcessing] = useState(false)
  const [allowRedirect, setAllowRedirect] = useState(false)

  const disabled = useMemo(() => {
    return (
      !cardholderName.trim().length ||
      cardNumber.replace(/\s+/g, "").length < 16 ||
      !/^\d{2}\/\d{2}$/.test(expiry) ||
      !/^\d{3,4}$/.test(cvv)
    )
  }, [cardholderName, cardNumber, expiry, cvv])

  useEffect(() => {
    if (!flow.hydrated || session.loading) {
      setAllowRedirect(false)
      return
    }

    if (!allowRedirect) {
      setAllowRedirect(true)
      return
    }

    if (!flow.hasSignupContext) {
      navigate("/auth/signup", { replace: true })
    }
  }, [allowRedirect, flow.hydrated, flow.hasSignupContext, navigate, session.loading])

  useEffect(() => {
    if (!flow.hydrated) {
      return
    }
    if (flow.paymentComplete) {
      navigate("/onboarding/questionnaire", { replace: true })
    }
  }, [flow.hydrated, flow.paymentComplete, navigate])

  const handleCheckout = () => {
    if (processing || disabled) {
      return
    }
    setProcessing(true)
    window.setTimeout(() => {
      flow.markPaymentComplete()
      setProcessing(false)
      navigate("/onboarding/questionnaire", { replace: true })
    }, 900)
  }

  const handleAutofill = () => {
    setCardNumber(mockCardNumber)
    setExpiry(mockExpiry)
    setCvv(mockCvv)
    if (!cardholderName.trim()) {
      setCardholderName(flow.tenantName ?? "Demo Restaurateur")
    }
  }

  const walletButtons = useMemo(
    () =>
      [
        {
          id: "paypal",
          label: t("onboarding:payment.walletOptions.paypal", { defaultValue: "PayPal" }),
          icon: <WalletMinimal className="mr-2 h-5 w-5" aria-hidden="true" />,
        },
        {
          id: "apple",
          label: t("onboarding:payment.walletOptions.apple", { defaultValue: "Pay" }),
          icon: <Apple className="mr-2 h-5 w-5" aria-hidden="true" />,
        },
        {
          id: "google",
          label: t("onboarding:payment.walletOptions.google", { defaultValue: "Pay" }),
          icon: <Globe className="mr-2 h-5 w-5" aria-hidden="true" />,
        },
      ] as const,
    [t]
  )

  if (!flow.hydrated) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" aria-hidden="true" />
        <span className="sr-only">
          {t("onboarding:payment.loading", { defaultValue: "Loading payment step" })}
        </span>
      </div>
    )
  }

  if (!flow.hasSignupContext) {
    return null
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <RuixenPaymentCard
        walletButtons={walletButtons.map((button) => ({
          ...button,
          onClick: handleAutofill,
          disabled: processing,
        }))}
        separatorLabel={t("onboarding:payment.separatorLabel", {
          defaultValue: "or pay using credit card",
        })}
        cardholder={{
          id: "cardholder-name",
          name: "cardholderName",
          label: t("onboarding:payment.cardholderLabel", {
            defaultValue: "Card holder full name",
          }),
          placeholder: t("onboarding:payment.cardholderPlaceholder", {
            defaultValue: "Enter your full name",
          }),
          value: cardholderName,
          onChange: setCardholderName,
          disabled: processing,
        }}
        cardNumber={{
          id: "card-number",
          name: "cardNumber",
          label: t("onboarding:payment.cardNumberLabel", { defaultValue: "Card Number" }),
          placeholder: t("onboarding:payment.cardNumberPlaceholder", {
            defaultValue: mockCardNumber,
          }),
          value: cardNumber,
          onChange: setCardNumber,
          inputMode: "numeric",
          disabled: processing,
        }}
        expiryCvvLabel={t("onboarding:payment.expiryCvvLabel", {
          defaultValue: "Expiry Date / CVV",
        })}
        expiry={{
          id: "expiry",
          name: "expiryDate",
          label: "",
          placeholder: t("onboarding:payment.expiryPlaceholder", {
            defaultValue: mockExpiry,
          }),
          value: expiry,
          onChange: setExpiry,
          inputMode: "numeric",
          disabled: processing,
        }}
        cvv={{
          id: "cvv",
          name: "cvv",
          label: "",
          placeholder: t("onboarding:payment.cvvPlaceholder", {
            defaultValue: mockCvv,
          }),
          value: cvv,
          onChange: setCvv,
          inputMode: "numeric",
          type: "password",
          disabled: processing,
        }}
        checkout={{
          label: t("onboarding:payment.submit", { defaultValue: "Checkout" }),
          onClick: handleCheckout,
          disabled: processing || disabled,
        }}
      />
    </div>
  )
}

export default PaymentPage
